// Import Required libraries
import React, { useContext, useMemo, useState } from "react";
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
} from "@mui/material";
import { makeStyles } from "@material-ui/core";
import { connect } from "react-redux";
import { ThemeContext } from "../../contexts/ThemeContext";
// Import images
import renameIcon from "../../../assets/images/renameIcon.svg";
import deleteIcon from "../../../assets/images/deleteIcon.svg";
import editIcon from "../../../assets/images/editIcon.svg";
// Imports Custom Components
import SigviewSwitch from "../../components/Common/SigviewSwitch";
import { ReportNameChange } from "./ReportHelpers";
// Import Actions and Reducers
import { masterTrackGaEvent } from "../../services/ga";
import { updateDialogInfo, updateUserScreen } from "../../redux/actions";
// Import API's
import {
  deleteSavedReport,
  deleteScheduledReport,
  updateScheduledReport,
} from "../../services/api";
// Import Config
import { config } from "../../config/config";
import NoSavedReport from "../../../assets/images/NoSavedReport.svg";
import SigviewTooltip from "../../components/Common/SigviewTooltip";

const useStyles = makeStyles(() => ({
  mainContainer: {
    padding: "0px 25px 10px",
  },
  tableRow: {
    cursor: "pointer",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  noDataCell: {
    borderBottom: "none",
    fontSize: "0.85rem",
    padding: "12px",
  },
}));

const ReportTable = ({
  data = [],
  rowKey = "id",
  reportType = "",
  user,
  onRowClick = () => {},
  isActive = false,
  searchQuery = "",
  setActiveReport,
  reloadData,
  onToggle,
  dispatch: ReduxDispatcher,
  variant = "report",
}) => {
  const classes = useStyles();
  const [reportActive, setReportActive] = useState({
    status: "success",
    message: "",
    data: isActive,
  });
  const [isLoading, setIsLoading] = useState(false);
  const reportMessages = config.messages.reports;
  const { state: themeState } = useContext(ThemeContext);
  const themeColors = themeState.themes[themeState.activeTheme];

  const handleRenameDialog = (report) => {
    const initialName = report.title;
    const payload = {
      open: true,
      children: (
        <ReportNameChange
          ReduxDispatcher={ReduxDispatcher}
          initialName={initialName}
          report={report.payload}
          reportType={reportType}
          user={user}
          setActiveReport={setActiveReport}
          reloadData={reloadData}
        />
      ),
    };
    const action = updateUserScreen("sigviewDialog", payload);
    ReduxDispatcher(action);
  };
  const onToggleSwitch = (event, checked, report) => {
    event.stopPropagation();

    masterTrackGaEvent({
      category: "ReportManager",
      action: "ScheduledToggle",
      label: checked ? "On" : "Off",
    });

    setReportActive((prevState) => ({ ...prevState, status: "loading" }));

    const fetchPayload = {
      ...report.payload, // use the payload from that specific row/report
      reportStatus: checked ? "active" : "inactive",
      statusToggleTime: Date.now().toString(),
    };

    updateScheduledReport({ payload: fetchPayload })
      .then(() => {
        setReportActive({ status: "success", message: "", data: checked });
        onToggle(report.id, fetchPayload);
      })
      .catch((error) => {
        if (error.error !== config.hardCoded.queryCancelled) {
          setReportActive((prevState) => ({ ...prevState, status: "success" }));
        }
      });
  };

  const handleGAButtonClick = (label) => {
    // Google Analytics Event - Master
    masterTrackGaEvent({
      category: "ReportManager",
      action: "ButtonClicked",
      label,
    });
  };
  const handleDeleteSavedReport = (report) => {
    handleGAButtonClick("DeleteSaved");
    //Set is loading to true
    setIsLoading(true);

    //Update snackbar
    let snackbarPayload = {
      ...user.screen.snackbar,
      open: true,
      message: reportMessages.reportDeleteRequest,
    };
    ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

    //Making fetch call
    const reportId = report.id;
    const fetchProps = {
      url: `${user.apiEndpoints.baseUrl}${config.apiEndpoints.deleteSavedReport}/${reportId}`,
    };
    const deleteSavedReportPromise = deleteSavedReport(fetchProps);
    deleteSavedReportPromise
      .then((responseDate) => {
        //Update snackbar
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: reportMessages.reportDeleteSuccess,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
        if (variant === "adminReport") {
          ReduxDispatcher(updateUserScreen("adminReport", false));
        }

        if (variant === "report") {
          //Close the active report
          setActiveReport({ type: "none", id: "" });

          //Reload data
          reloadData("saved");
        }
      })
      .catch((json) => {
        console.log("API FAILED", json);
        setIsLoading(false);
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: json.error || reportMessages.reportDeleteFailed,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
      });
  };
  const handleDeleteScheduledReport = (report) => {
    handleGAButtonClick("DeleteScheduled");

    //Set is loading to true
    setIsLoading(true);

    //Update snackbar
    let snackbarPayload = {
      ...user.screen.snackbar,
      open: true,
      message: reportMessages.reportDeleteRequest,
    };
    ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

    //Making fetch call
    const reportId = report.id;
    const fetchProps = {
      url: `${user.apiEndpoints.baseUrl}${config.apiEndpoints.deleteScheduledReport}/${reportId}`,
    };
    const deleteScheduledReportPromise = deleteScheduledReport(fetchProps);
    deleteScheduledReportPromise
      .then((responseDate) => {
        //Update snackbar
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: reportMessages.reportDeleteSuccess,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));

        if (variant === "adminReport") {
          ReduxDispatcher(updateUserScreen("adminReport", false));
        }

        if (variant === "report") {
          //Close the active report
          setActiveReport({ type: "none", id: "" });

          //Reload data
          reloadData("scheduled");
        }
      })
      .catch((json) => {
        console.log("API FAILED", json);
        setIsLoading(false);
        let snackbarPayload = {
          ...user.screen.snackbar,
          open: true,
          message: json.error || reportMessages.reportDeleteFailed,
        };
        ReduxDispatcher(updateUserScreen("snackbar", snackbarPayload));
      });
  };
  const handleDeleteReportDialog = (reportType, report) => {
    const message = {
      title: reportMessages.reportDeleteConfirmation,
      subtitle: "",
    };
    const handleNo = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
        })
      );
    };
    const handleYes = () => {
      //Close the dialog box
      ReduxDispatcher(
        updateDialogInfo({
          ...user.dialogInfo,
          open: false,
          message: { title: "", subtitle: "" },
        })
      );
      if (reportType === "saved") {
        handleDeleteSavedReport(report);
      } else if (reportType === "scheduled") {
        handleDeleteScheduledReport(report);
      }
    };
    ReduxDispatcher(
      updateDialogInfo({
        ...user.dialogInfo,
        open: true,
        message,
        handleYes,
        handleNo,
      })
    );
  };
  const renderActions = (type, report) => {
    const actionHandlers = {
      [renameIcon]: () => handleRenameDialog(report),
      [deleteIcon]: () => handleDeleteReportDialog(type, report),
      [editIcon]: () => onRowClick?.({ type: type, id: report.id }),
    };

    const iconsByType = {
      saved: [renameIcon, deleteIcon, editIcon],
      scheduled: [deleteIcon, editIcon],
      download: [editIcon],
    };
    const iconTooltips = {
      [renameIcon]: "Rename",
      [deleteIcon]: "Delete",
      [editIcon]: "Edit",
    };
    return (
      <Box display="flex" gap={2} justifyContent="center">
        {type === "scheduled" && (
          <SigviewSwitch
            checked={report.payload?.reportStatus === "active"}
            onChange={(e, checked) => onToggleSwitch(e, checked, report)}
            customClassName="scheduleReportON-OFF-GA"
          />
        )}
        {iconsByType[type]?.map((icon, index) => (
          <SigviewTooltip title={iconTooltips[icon]} key={index}>
            <img
              key={index}
              src={icon}
              alt="Action"
              onClick={(e) => {
                e.stopPropagation();
                actionHandlers[icon]?.(report);
              }}
              style={{ cursor: "pointer" }}
            />
          </SigviewTooltip>
        ))}
      </Box>
    );
  };

  const getColumns = () => [
    {
      id: "name",
      label: "Report Name",
      render: (row) =>
        row.name ||
        row.title || (
          <Box component="span" sx={{ opacity: 0.5 }}>
            —
          </Box>
        ),
    },
    {
      id: "email",
      label: "Created By",
      width: "40%",
      align: "center",
      render: (row) =>
        row.email ||
        row.payload?.loginInfo?.providerKey ||
        row.payload?.email || (
          <Box component="span" sx={{ opacity: 0.5 }}>
            —
          </Box>
        ),
    },
    {
      id: "createdOn",
      label: "Created On",
      width: "30%",
      align: "center",
      render: (row) => {
        const raw = row.createdOn;
        const formatted =
          typeof raw === "string"
            ? raw
            : new Date(raw).toLocaleString("en-IN", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              });

        return (
          formatted || (
            <Box component="span" sx={{ opacity: 0.5 }}>
              —
            </Box>
          )
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      align: "center",
      width: "15%",
      render: (row) => renderActions(reportType, row),
    },
  ];

  const columns = getColumns();
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return data.data;

    const lower = searchQuery.toLowerCase();
    return data.data.filter((item) =>
      item.title?.toLowerCase().includes(lower)
    );
  }, [searchQuery, data.data]);

  return (
    <Box className={classes.mainContainer} pt={2}>
      <TableContainer component={Paper} elevation={0}>
        <Table sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow
              sx={{ backgroundColor: "#EEEFF2" }}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{
                    padding: "6px 16px",
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                    textAlign: col.align || "left",
                    width: col.width || `${100 / columns.length}%`,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    borderBottom: "none",
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((row) => (
                <TableRow
                  key={row[rowKey]}
                  hover
                  onClick={() => onRowClick?.({ type: reportType, id: row.id })}
                  className={classes.tableRow}
                >
                  {columns.map((col) => {
                    const content = col.render
                      ? col.render(row)
                      : row[col.id] || (
                          <Box component="span" sx={{ opacity: 0.5 }}>
                            —
                          </Box>
                        );

                    return (
                      <TableCell
                        key={col.id}
                        sx={{
                          padding: "8px 16px",
                          textAlign: col.align || "left",
                          width: col.width || `${100 / columns.length}%`,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          fontSize: "0.80rem",
                          borderBottom: "none",
                        }}
                        title={
                          typeof content === "string" ? content : undefined
                        }
                      >
                        {content}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow sx={{ borderBottom: "none" }}>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  className={classes.noDataCell}
                  sx={{ borderBottom: "none" }} // <-- removes bottom border
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <img
                      src={NoSavedReport}
                      alt="No records found"
                      style={{
                        width: "300px",
                        height: "auto",
                        marginBottom: "12px",
                        marginTop: "120px",
                      }}
                    />
                    <p
                      style={{ color: "#6c757d", fontSize: "16px", margin: 0 }}
                    >
                      No records found
                    </p>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
const mapStateToProps = (state) => ({
  user: state.user,
  allData: state.data,
  globalFilters: state.globalFilters,
});

export default connect(mapStateToProps)(ReportTable);
